import React, { useEffect, useState } from "react";
import styles from "./statistic.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { statistic5 } from "services/statistic/statistic5";
import { BlockStatusFacultyData, YearlyOccupancyBlock } from "modules/statistic";
import { generateCSVContent, downloadFile } from "utils/statistic";
import commonStyles from "components/commonStyle/commonStyle.module.css";
import ErrorFailed from "components/errorFailed/errorFailed";
import Popup from "components/Popup/popup";

export default function Statistic5() {
  const [blockData, setBlockData] = useState<BlockStatusFacultyData>({});
  const [occupancyBlockData, setOccupancyBlockData] = useState<YearlyOccupancyBlock>({});
  const [blocks, setBlocks] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);
  const fetchData = async () => {
  try {
    const res = await statistic5(startDate, endDate);

    if (res.success && res.data && res.occupancy) {
      // 1. استخرج البيانات الأساسية وبيانات الإشغال
      const data = res.data as BlockStatusFacultyData;
      const dataOccupancy = res.occupancy as YearlyOccupancyBlock;

      // 2. خزّن بيانات الكتل (blocks)
      setBlockData(data);

      // 3. جمع كل السنوات وكل البلوكات الموجودة في البيانات
      const blockSet = new Set<string>();
      const allYears: number[] = [];

      Object.entries(data).forEach(([status, yearData]) => {
        Object.entries(yearData).forEach(([yearStr, yearValue]) => {
          const year = Number(yearStr);
          allYears.push(year);
          yearValue.blocks.forEach(block => {
            blockSet.add(block.block);
          });
        });
      });

      if (allYears.length > 0) {
        setMinYear(Math.min(...allYears));
        setMaxYear(Math.max(...allYears));
      }

      const allBlocks = Array.from(blockSet).sort();

      // 4. بناء بيانات الإشغال مع إضافة البلوكات الناقصة ونسبة إشغال صفر
      const updatedOccupancyData: YearlyOccupancyBlock = {};

      Object.entries(dataOccupancy).forEach(([year, yearData]) => {
        // نسخ المصفوفة الأصلية من البلوكات
        const yearBlocks = [...yearData.blocks];
        const existingBlocks = new Set(yearBlocks.map(block => block.block));

        // أضف البلوكات التي ليست موجودة في البيانات مع نسبة إشغال صفر
        allBlocks.forEach(block => {
          if (!existingBlocks.has(block)) {
            yearBlocks.push({ block, occupancy_percentage: 0 });
          }
        });

        // حساب المتوسط مع تجاهل القيم صفرية الإشغال
        const positiveBlocks = yearBlocks.filter(b => b.occupancy_percentage > 0);
        const totalPositive = positiveBlocks.reduce((sum, b) => sum + b.occupancy_percentage, 0);
        const average = positiveBlocks.length > 0 ? totalPositive / positiveBlocks.length : 0;

        updatedOccupancyData[year] = {
          blocks: yearBlocks,
          average_occupancy: Number(average.toFixed(2)),
        };
      });

      // 5. تأكد من وجود بيانات لكل سنة في النطاق (أضف السنوات الناقصة بالقيم صفر)
      allYears.forEach(year => {
        if (!updatedOccupancyData[year]) {
          updatedOccupancyData[year] = {
            blocks: allBlocks.map(block => ({ block, occupancy_percentage: 0 })),
            average_occupancy: 0,
          };
        }
      });

      // 6. ضبط الحالة (state) بالبيانات الجديدة
      setOccupancyBlockData(updatedOccupancyData);
      setBlocks(allBlocks);

    } else {
      console.error("Fehler beim Laden der Daten:", res.message || "Unbekannter Fehler");
    }
  } catch (error) {
    console.error("Fehler beim Laden der Daten:", error);
  }
};


  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError("Bitte geben Sie sowohl ein Startdatum als auch ein Enddatum ein.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Das Startdatum kann nicht nach dem Enddatum liegen.");
      return;
    }

    setIsPopupOpen(false);
    setError(null);
    setIsPopupOpen(false);
    fetchData();
  };

  const handleDeleteFilter = () => {
    setStartDate('');
    setEndDate('');
    setError(null)
    setIsPopupOpen(false);
  };

  const downloadCSV = () => {
    const headers = ['Status', 'Jahr', ...blocks.flatMap((block) => [
      `Block ${block}`,
      `Alter bis 1 Jahr`
    ]), 'Gesamt', 'Alter bis 1 Jahr', 'in % v. Gesamt', 'Kundengruppen Anteil'];

    const rows: (string | number)[][] = [];

    Object.entries(blockData).forEach(([status, yearsData]) => {
      const currentYear = Math.max(...Object.keys(yearsData).map(Number));

      Object.entries(yearsData)
        .filter(([year]) => status && year)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .forEach(([year, yearData]) => {
          const row = [
            status,
            year,
            ...blocks.flatMap((block) => {
              const blockDataForYear = yearData.blocks.find((b) => b.block === block);

              return [
                blockDataForYear ? blockDataForYear.total_bookings : 0,
                blockDataForYear ? blockDataForYear.under_one_year : 0
              ];
            }),
            yearData.total,
            yearData.under_one_year,
            yearData.percentage_under_one_year.toFixed(2),
            (yearData.percentage ?? 0).toFixed(2)
          ];

          rows.push(row);
        });
    });

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Anlage3.csv");
  };

  useEffect(() => {
    fetchData();
  }, [isPopupOpen]);

  return (
    <>
      <StatisticFormat
        label="Anlage 3"
        description={startDate && endDate
          ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der Buchungen je Block (Montag bis Freitag).`
          : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der Buchungen je Block (Montag bis Freitag).`
        }
        onButton1Click={downloadCSV}
        onFilterButton1Click={() => setIsPopupOpen(true)}

      >
        <div className={styles.container}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Jahr</th>
                {blocks.map((block) => (
                  <React.Fragment key={`${block}`}>
                    <th>Block {block}</th>
                    <th>Alter bis 1 Jahr</th>
                  </React.Fragment>
                ))}
                <th className={styles.gesamt}>Gesamt</th>
                <th>Alter bis 1 Jahr</th>
                <th>in % v. Gesamt</th>
                <th className={styles.gesamt}>Kundengruppen Anteil</th>
              </tr>
            </thead>
            <tbody>
              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={8}></td>
              </tr>
              {Object.entries(blockData).map(([status, yearsData]) => {
                const currentYear = Math.max(...Object.keys(yearsData).map(Number));

                return (
                  <React.Fragment key={status}>
                    {Object.entries(yearsData)
                      .filter(([year]) => status && year)
                      .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                      .map(([year, yearData]) => (
                        <tr
                          key={`${status}-${year}`}
                          className={styles[`year-${(currentYear - parseInt(year)) % 9 + 1}`]}
                        >
                          <td>{status}</td>
                          <td>{year}</td>

                          {blocks.map((block) => {
                            const blockDataForYear = yearData.blocks.find((b) => b.block === block);
                            return (
                              <React.Fragment key={`${status}-${year}-${block}`}>
                                <td>
                                  {blockDataForYear ? blockDataForYear.total_bookings : 0}
                                </td>
                                <td>
                                  {blockDataForYear ? blockDataForYear.under_one_year : 0}
                                </td>
                              </React.Fragment>
                            );
                          })}

                          <td>{yearData.total}</td>
                          <td>{yearData.under_one_year}</td>
                          <td>{yearData.percentage_under_one_year.toFixed(2)}%</td>
                          <td>{(yearData.percentage ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}

                    <tr className={styles.emptyRowNoBorder}>
                      <td colSpan={8}></td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={8}></td>
              </tr>
              {Object.entries(occupancyBlockData)
                .filter(([year]) => year && year !== '0')
                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                .map(([year, yearData]) => {
                  const currentYear = Math.max(...Object.keys(occupancyBlockData).map(Number));
                  return (
                    <tr key={year} className={styles[`year-${(currentYear - parseInt(year)) % 9 + 1}`]}>
                      <td>ِAuslastung</td>
                      <td>{year}</td>

                      {blocks.map((block) => {
                        const blockDataForYear = yearData.blocks.find((b) => b.block === block);
                        return (
                          <React.Fragment key={`${year}-${block}`}>
                            <td>{blockDataForYear ? blockDataForYear.occupancy_percentage.toFixed(2) : '0.00'}</td>
                            <td></td>
                          </React.Fragment>
                        );
                      })}

                      <td colSpan={2} style={{ fontWeight: 'bold', textAlign: 'right' }}>
                        {yearData.average_occupancy.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}


            </tbody>
          </table>
        </div>
      </StatisticFormat>
      <Popup
        title={'Filter'}
        submitLabel={"Einsetzen"}
        closeLabel={"Zurücksetzen"}
        isWithButton={true}
        isOpen={isPopupOpen}
        onClose={handleDeleteFilter} onSubmit={handleFilter}>
        {error && <ErrorFailed error={error} />}

        <div className={commonStyles.formGroupRow}>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </Popup>
    </>
  );
}
